/**
 * konexBrain — "cerebro" de Konex, Fase A (guionado puro).
 *
 * Función pura: recibe contexto del wizard y devuelve el mensaje que Konex
 * debe decir en ese paso, o `null` si debe callarse (paso ya visto o sin guion).
 *
 * Contrato: docs/KONEX_APP_ROADMAP.md §3.4 · Guion: §3.4.1 (texto chileno "tú").
 * Fase B reemplaza el CUERPO de esta función para leer `useBooking().state`;
 * la firma y `<KonexGuide />` no cambian.
 */

import type { BookingStep } from '../BookingWizard';

export type KonexTone = 'contenido' | 'calido' | 'jugueton';

export interface KonexContext {
  step: BookingStep;
  isExisting: boolean;
  seenSteps: BookingStep[];
  /** Fase B (docs/plan-ruta-terapeutica-pareja.md §6.3) — true = falta nombre_pareja y corresponde pedirlo. */
  parejaNudge?: boolean;
}

export interface KonexScriptedMessage {
  id: string;
  text: string;
  tone: KonexTone;
}

/** `{ id, text, tone } | null` — `null` = Konex se calla. */
export type KonexMessage = KonexScriptedMessage | null;

/**
 * Guion Fase A — texto TAL CUAL de docs/KONEX_APP_ROADMAP.md §3.4.1 (líneas 142-147).
 * El guion solo declara la INTENCIÓN vía `tone`; la traducción a color de acento
 * y a intensidad de animación la hace `<KonexGuide />`.
 */
const KONEX_SCRIPT: Partial<Record<BookingStep, KonexScriptedMessage>> = {
  verification: {
    id: 'verification',
    text: 'Hola, soy Konex 👋 Te acompaño en la reserva. Partamos con tu RUT: lo usamos solo para ver si ya tienes una ficha con nosotros.',
    tone: 'contenido',
  },
  'existing-patient': {
    id: 'existing-patient',
    text: '¡Qué bueno tenerte de vuelta! Este camino es más corto: eliges terapeuta, día y hora, y listo.',
    tone: 'calido',
  },
  'service-therapist': {
    id: 'service-therapist',
    text: 'Ahora la parte entretenida: elige el tipo de sesión y con quién atenderte. ¿No estás seguro? Cada terapeuta tiene su perfil, dale una mirada.',
    tone: 'jugueton',
  },
  calendar: {
    id: 'calendar',
    text: 'Elige el día y la hora que más te acomoden. Si tu terapeuta no tiene cupos esta semana, prueba la siguiente.',
    tone: 'jugueton',
  },
  'patient-confirmation': {
    id: 'patient-confirmation',
    text: 'Último paso. Tus datos de contacto los ve únicamente tu terapeuta y no se comparten con nadie más. Revisa que tu correo esté bien escrito: ahí llega la confirmación.',
    tone: 'contenido',
  },
  success: {
    id: 'success',
    text: '¡Listo! 🎉 Diste el primer paso, y no es poco. Te enviamos todo por correo. Nos vemos pronto.',
    tone: 'jugueton',
  },
};

export function konexBrain(ctx: KonexContext): KonexMessage {
  // Fase B — nudge de nombre_pareja (§6.3): toma prioridad sobre el guion normal
  // de 'existing-patient'. Reusa el MISMO gate de deduplicación que el resto del
  // guion (seenSteps.includes(step)) en vez de inventar un mecanismo paralelo —
  // una vez mostrado (nudge u otro), el paso queda marcado visto y no se repite.
  if (ctx.step === 'existing-patient' && ctx.parejaNudge && !ctx.seenSteps.includes(ctx.step)) {
    return {
      id: 'pareja-nudge',
      text: 'Antes de seguir: nos falta el nombre de tu pareja. Si estás en terapia de parejas, agregalo en la tarjeta de arriba — así personalizamos tu proceso.',
      tone: 'contenido',
    };
  }
  if (ctx.seenSteps.includes(ctx.step)) return null;
  return getScriptedMessage(ctx.step);
}

/**
 * Mensaje guionado de un paso IGNORANDO si ya se vio. `konexBrain` devuelve
 * `null` para un paso ya visto; esto permite reconstruir el avatar colapsado /
 * descartado tras recargar (spec §2 fila "Descartado" · §10.4): el avatar y su
 * último mensaje persisten aunque no haya nada nuevo que decir.
 */
export function getScriptedMessage(step: BookingStep): KonexScriptedMessage | null {
  return KONEX_SCRIPT[step] ?? null;
}
